<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  inject,
  getCurrentInstance,
  onMounted,
  onUnmounted,
} from "vue";
import {
  detectCardType,
  cardMaskByType,
  buildMaskedCardNumber,
} from "@/utils/creditCard";
import { Address } from "@/models/dto";
import { FormattedPaymentMethod } from "../models/FormattedPaymentMethod";
import AddressPicker from "@/components/AddressPicker.vue";
import { finixConfig } from "@/utils/env";

const props = defineProps<{
  showCancel?: boolean;
  disputedCheckout?: boolean;
  value?: FormattedPaymentMethod;
  hideSaveForLater?: boolean;
}>();


const emit = defineEmits(["cancel", "input", "update:form-errors"]);

const isInGuestCheckout = inject("isInGuestCheckout", false);

const cardFormRef = ref();
const addressFormRef = ref();
const addressPickerRef = ref();

const nameOnCard = ref("");
const cardNumber = ref("");
const expiration = ref("");
const cvv = ref("");
const saveCard = ref(false);
const address = ref<Partial<Address>>({});

const { proxy } = getCurrentInstance()!;
const $t = proxy.$t.bind(proxy);

const finixForm = ref<any>(null);

const cardNumberMask = computed(() => {
  if (props.disputedCheckout) {
    return null;
  }
  return cardMaskByType(cardType.value);
});

const rawCardNumber = computed(() => cardNumber.value.replace(/\D/g, ""));

const cardType = computed(() => detectCardType(rawCardNumber.value));

const cardIconName = computed(() => {
  if (cardType.value === "default") {
    return "default_card";
  }
  return cardType.value;
});

const lastFourDigits = computed(() =>
  rawCardNumber.value.substr(rawCardNumber.value.length - 4, 4)
);

const formatData = (): FormattedPaymentMethod => {
  const {
    addressName,
    street1,
    street2,
    city,
    state,
    postalCode,
    lat,
    lng,
    title,
    timeZone,
    country,
  } = address.value;

  return {
    activeMethod: "credit_card",
    showOnCharterUp: saveCard.value,
    name: nameOnCard.value,
    cardholderName: nameOnCard.value,
    cardNumber: rawCardNumber.value,
    mask: lastFourDigits.value,
    securityCode: cvv.value,
    exp_date: expiration.value,
    expirationMonth: expiration.value?.split("/")?.[0] || "",
    expirationYear: expiration.value?.split("/")?.[1] || "",
    type_label: cardType.value,
    address: {
      name: addressName,
      street1,
      street2,
      city,
      state,
      postalCode,
      lat,
      lng,
      title,
      time_zone: timeZone,
      country,
    },
  };
};

const formattedData = computed<FormattedPaymentMethod>(() => formatData());

const expirationRules = [
  (v: string) => !!v || $t("creditCardForm.EXPIRATION.RULES.REQUIRED"),
  (v: string) =>
    /^(0[1-9]|1[0-2])\/(\d{2}|\d{4})$/.test(v) ||
    $t("creditCardForm.EXPIRATION.RULES.VALID"),
  (v: string) =>
    isDateInFuture(v) || $t("creditCardForm.EXPIRATION.RULES.FUTURE"),
];

watch(
  () => props.value,
  (newValue) => {
    if (!props.disputedCheckout) return;
    if (!newValue) return;
    nameOnCard.value = newValue?.name || newValue?.cardholderName || "";
    cardNumber.value =
      buildMaskedCardNumber(newValue?.mask, newValue?.type_label) || "";
    expiration.value = newValue?.exp_date || "";
    cvv.value = newValue?.securityCode || "";
    address.value = {
      name: newValue?.address?.name || "",
      street1: newValue?.address?.street1 || "",
      street2: newValue?.address?.street2 || "",
      city: newValue?.address?.city || "",
      state: newValue?.address?.state || "",
      // @ts-ignore
      postalCode:
        newValue?.address?.postalCode || newValue?.address?.postal_code || "",
      title: newValue?.address?.title || "",
      country: newValue?.address?.country || "",
    };
  },
  { immediate: true }
);

watch(formattedData, (val) => {
  if (props.disputedCheckout) return;
  emit("input", val);
});

const finixErrors = ref<string[]>([]);
const addressFormValid = ref(false);
const cardFormValid = ref(false);

const emitFormErrors = () => {
  const hasErrors =
    finixErrors.value.length > 0 ||
    !cardFormValid.value ||
    !addressFormValid.value;
  emit("update:form-errors", hasErrors);
};

const updateFinixErrors = () => {
  if (finixForm.value && finixForm.value.state) {
    const errors: string[] = [];
    Object.values(finixForm.value.state).forEach((field: any) => {
      if (
        Array.isArray(field.errorMessages) &&
        field.errorMessages.length > 0
      ) {
        errors.push(...field.errorMessages);
      }
    });
    finixErrors.value = errors;
    emitFormErrors();
  }
};

watch(addressFormValid, emitFormErrors);
watch(cardFormValid, emitFormErrors);

let finixErrorInterval: number | undefined;

onMounted(() => {
  initFinixForm();
  if (!props.disputedCheckout) {
    finixErrorInterval = window.setInterval(updateFinixErrors, 1000);
  }
});

onUnmounted(() => {
  if (finixErrorInterval) clearInterval(finixErrorInterval);
});

const initFinixForm = async () => {
  const windowWithFinix = window as any;
  if (!windowWithFinix.Finix) {
    console.warn("Failed to load Finix. Attempting to reload...");
    await proxy.$loadScript("https://js.finix.com/v/1/finix.js");
  }
  finixForm.value = windowWithFinix.Finix.CardTokenForm(
    "finix-form",
    finixOptions
  );
};

const getTokenizedFinixInfo = async (): Promise<string> => {
  const { environment, applicationId } = finixConfig();
  return new Promise((resolve, reject) => {
    finixForm.value.submit(environment, applicationId, function (err, res) {
      if (err) {
        reject(err);
      } else {
        const tokenData = res.data || {};
        const token = tokenData.id;
        resolve(token);
      }
    });
  });
};

const isDateInFuture = (expirationDate: string): boolean => {
  const [month, year] = expirationDate.split("/").map(Number);
  if (isNaN(month) || isNaN(year)) {
    return false;
  }
  let yearString = year.toString();
  if (yearString.length === 2) {
    yearString = `20${year}`;
  }
  const lastDayOfMonth = proxy
    .$dayjs(`${yearString}-${month.toString().padStart(2, "0")}`)
    .endOf("month");
  const currentDate = proxy.$dayjs().startOf("day");
  return lastDayOfMonth.isSameOrAfter(currentDate);
};

const cancel = () => {
  emit("cancel");
  reset();
};

const reset = () => {
  nameOnCard.value = "";
  cardNumber.value = "";
  expiration.value = "";
  cvv.value = "";
  saveCard.value = false;
  address.value = {};
  const addressPicker = addressPickerRef.value as any;
  if (addressPicker && addressPicker.clearAddress) {
    addressPicker.clearAddress();
  }
};

const validate = (): boolean => {
  const cardForm = cardFormRef.value as any;
  const cardFormValid = cardForm.validate();
  const addressForm = addressFormRef.value as any;
  const addressFormValid = addressForm.validate();
  let firstInvalidInput = null;
  cardForm.inputs.some((input: any) => {
    if (!input.valid && !firstInvalidInput) {
      firstInvalidInput = input;
    }
    return !input.valid;
  });
  addressForm.inputs.some((input: any) => {
    if (!input.valid && !firstInvalidInput) {
      firstInvalidInput = input;
    }
    return !input.valid;
  });
  if (firstInvalidInput) {
    proxy.$vuetify.goTo(firstInvalidInput.$el.offsetTop);
  }
  return addressFormValid && cardFormValid;
};

const finixOptions = {
  showAddress: false,
  showLabels: true,
  labels: {
    name: "Name",
    security_code: "Security Code",
    address_line1: "Address",
  },
  showPlaceholders: false,
  hideFields: [
    "address_line1",
    "address_line2",
    "address_state",
    "address_city",
    "address_postal_code",
    "address_region",
    "address_country",
  ],
  requiredFields: ["name"],
  hideErrorMessages: false,
  errorMessages: {
    name: "Name is required",
    number: "Card number is required",
    security_code: "Security code is required",
  },
  styles: {
    default: {
      color: "#3c4853",
      border: "1px solid #a0aeba",
      borderRadius: "5px",
      padding: "12px",
      fontFamily: "'Inter Bold', Arial, sans-serif",
      fontWeight: "normal",
      fontSize: "16px",
      overflow: "hidden",
      height: "42px",
    },
    error: {
      color: "#3c4853",
    },
  },
};

defineExpose({
  getTokenizedFinixInfo,
  validate,
  reset,
  "card-form": cardFormRef,
  "address-form": addressFormRef,
});
</script>

<template>
  <span>
    <v-form ref="cardFormRef" v-model="cardFormValid" class="margin-x-2">
      <div v-show="!disputedCheckout" id="finix-form" />

      <v-row wrap v-if="disputedCheckout">
        <v-col cols="12">
          <CUTextField
            v-model="nameOnCard"
            :label="$t('creditCardForm.NAME.LABEL')"
            hide-details="auto"
            :rules="[(v) => !!v || $t('creditCardForm.NAME.RULES.REQUIRED')]"
            autocomplete="cc-name"
            :tab-index="1"
            validate-on-blur
            required
          />
        </v-col>
        <v-col cols="12">
          <CUTextField
            v-model="cardNumber"
            v-mask="cardNumberMask"
            :label="$t('creditCardForm.NUMBER.LABEL')"
            :rules="[
              (v) => !!v || $t('creditCardForm.NUMBER.RULES.REQUIRED'),
              (v) => v.length >= 15 || $t('creditCardForm.NUMBER.RULES.VALID'),
            ]"
            hide-details="auto"
            autocomplete="cc-number"
            :tab-index="2"
            validate-on-blur
            required
          >
            <template #append>
              <CUIcon
                :key="cardIconName"
                view-box="0 0 24 24"
                class="fade-in margin-t-n1 margin-r-4 w-40 h-40 z-20 cursor-default"
              >
                {{ cardIconName }}
              </CUIcon>
            </template>
          </CUTextField>
        </v-col>
        <v-col cols="6">
          <CUTextField
            v-model="expiration"
            placeholder="MM/YY"
            :label="$t('creditCardForm.EXPIRATION.LABELV2')"
            :rules="expirationRules"
            hide-details="auto"
            autocomplete="cc-exp"
            :tab-index="3"
            validate-on-blur
            required
          />
        </v-col>
        <v-col cols="6">
          <CUTextField
            v-model="cvv"
            v-mask="'####'"
            :label="$t('creditCardForm.CVV.LABEL')"
            :rules="[
              (v) => !!v || $t('creditCardForm.CVV.RULES.REQUIRED'),
              (v) => v.length >= 3 || $t('creditCardForm.CVV.RULES.VALID'),
            ]"
            hide-details="auto"
            type="password"
            autocomplete="cc-csc"
            :tab-index="4"
            validate-on-blur
            required
          />
        </v-col>
      </v-row>
    </v-form>

    <v-form ref="addressFormRef" v-model="addressFormValid" class="margin-x-2">
      <v-row>
        <!-- Always show the address picker, even for Finix forms -->
        <v-col cols="12" class="pb-0">
          <AddressPicker
            ref="addressPickerRef"
            v-model="address"
            :disputedCheckout="disputedCheckout"
            is-payment-form
            required
            :label="$t('addressPicker.FORM.STREET1.LABEL')"
            :tab-index="5"
          />
        </v-col>
        <v-col
          v-if="!isInGuestCheckout && !disputedCheckout"
          cols="12"
          class="pt-0"
        >
          <div class="d-flex align-center">
            <v-btn
              v-if="showCancel"
              plain
              x-small
              color="gray"
              @click="cancel"
              class="padding-x-0 justify-start"
            >
              <CUIcon
                id="support-dialog-link-back"
                color="gray"
                view-box="0 0 24 24"
                :style="{ 'margin-left': '-10px' }"
                width="24"
                height="24"
              >
                keyboard_arrow_left
              </CUIcon>
              {{ $t("common.BACK") }}
            </v-btn>
            <v-spacer />
            <v-checkbox
              v-show="!hideSaveForLater"
              v-model="saveCard"
              :label="$t('creditCardForm.SAVE_FOR_LATER')"
              class="margin-t-n1 text-midnight-gray-900"
              hide-details
            />
          </div>
        </v-col>
      </v-row>
    </v-form>
  </span>
</template>

<style lang="scss">
.field-holder {
  margin-top: 10px !important;
}

.field-array {
  margin-bottom: 10px !important;
}

.field {
  font-family: "Inter Medium", Arial, sans-serif !important;
  font-weight: light !important;
  margin-bottom: -10px !important;

  .input {
    border: 3px solid rgb(160, 174, 186) !important;
  }
}

.validation {
  font-size: 12px !important;
}
</style>
